import { useState, useEffect, useRef } from "react";
import { useCart } from "../context/CartContext";
import { QRCodeSVG } from "qrcode.react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiUser, FiPhone, FiMail, FiMapPin, FiPackage, FiClock } from "react-icons/fi";
import { apiPlaceOrder, apiPlaceOrderGuest, apiGetOrderStatus } from "../services/api";
import "./Checkout.css";

const UPI_ID   = "rizzglam@kotak";
const UPI_NAME = "Rizzglam Private Limited";
const WHATSAPP = "918096554464";

const UPI_APPS = [
  { name: "GPay",    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Google_Pay_Logo.svg/512px-Google_Pay_Logo.svg.png",                                                                    scheme: "tez://upi/pay"   },
  { name: "PhonePe", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5f/PhonePe_Logo.svg/512px-PhonePe_Logo.svg.png",                                                                          scheme: "phonepe://pay"   },
  { name: "Paytm",   icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Paytm_Logo_%28standalone%29.svg/512px-Paytm_Logo_%28standalone%29.svg.png",                                            scheme: "paytmmp://pay"   },
  { name: "BHIM",    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/UPI-Logo-vector.svg/512px-UPI-Logo-vector.svg.png",                                                                     scheme: "upi://pay"       },
];

function buildUPILink(scheme, amount, note) {
  const p = new URLSearchParams({ pa: UPI_ID, pn: UPI_NAME, am: amount.toFixed(2), cu: "INR", tn: note });
  return `${scheme}?${p.toString()}`;
}

const STEPS = ["Shipping", "Payment", "Confirm"];

export default function Checkout() {
  const { cart, total, dispatch } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=shipping 1=payment 2=awaiting 3=approved 4=rejected
  const [orderId, setOrderId] = useState(null);
  const pollRef = useRef(null);

  // Shipping fields
  const [form, setForm] = useState({
    fullName: "", phone: "", email: "",
    addressLine: "", city: "", postalCode: "",
  });
  const [errors, setErrors] = useState({});

  // Payment
  const [selectedApp, setSelectedApp] = useState(null);
  const [paymentRef, setPaymentRef]   = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [waLink, setWaLink]           = useState("");
  const [showPopup, setShowPopup]     = useState(false);

  const note = `RizzGlam Order - ${cart.map(i => i.name).join(", ")}`;
  const upiLink = buildUPILink("upi://pay", total, note);

  // ── Poll order status every 5s after placing ──
  useEffect(() => {
    if (step === 2 && orderId) {
      pollRef.current = setInterval(async () => {
        try {
          const data = await apiGetOrderStatus(orderId);
          if (data.payment_status === "approved") { clearInterval(pollRef.current); setStep(3); }
          if (data.payment_status === "rejected") { clearInterval(pollRef.current); setStep(4); }
        } catch {}
      }, 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [step, orderId]);

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!form.fullName.trim())    e.fullName    = "Full name is required";
    if (!form.phone.trim())       e.phone       = "Phone number is required";
    else if (!/^[0-9+\s-]{10,15}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.addressLine.trim()) e.addressLine = "Address is required";
    if (!form.city.trim())        e.city        = "City is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: "" }));
  };

  // ── Step 1 → Step 2 ──
  const goToPayment = () => { if (validate()) setStep(1); };

  // ── Confirm Payment & Place Order ──
  const confirmPayment = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const orderData = {
        items: cart.map(i => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, size: i.size, color: i.color, image: i.image })),
        total,
        customer_name:  form.fullName,
        customer_phone: form.phone,
        customer_email: form.email || null,
        address_line:   form.addressLine,
        city:           form.city,
        province:       null,
        postal_code:    form.postalCode || null,
        payment_method: "UPI",
        upi_app:        selectedApp,
        payment_ref:    paymentRef || null,
      };

      const token = localStorage.getItem("rizzglam_token");
      const res = token ? await apiPlaceOrder(orderData) : await apiPlaceOrderGuest(orderData);
      if (res.error) { setSubmitError(res.error); setSubmitting(false); return; }

      // Build WhatsApp message link (user will send manually)
      const itemLines = cart.map(i =>
        `• ${i.name}${i.size ? ` (${i.size})` : ""}${i.color ? ` / ${i.color}` : ""} ×${i.qty} = Rs.${(i.price * i.qty).toLocaleString()}`
      ).join("\n");
      const msg = encodeURIComponent(
        `🛍️ *New Order #${res.id} — RizzGlam*\n\n` +
        `👤 *Customer:* ${form.fullName} · ${form.phone}\n` +
        (form.email ? `📧 ${form.email}\n` : "") +
        `📦 *Address:* ${form.addressLine}, ${form.city}${form.postalCode ? " - " + form.postalCode : ""}\n\n` +
        `🛒 *Items:*\n${itemLines}\n\n` +
        `💰 *Total: Rs. ${total.toLocaleString()}*\n` +
        `💳 *Paid via UPI* · UTR: *${paymentRef}*\n` +
        `🏦 *UPI App:* ${selectedApp || "UPI"}\n\n` +
        `✅ Payment done. Please verify UTR *${paymentRef}* and approve my order.`
      );
      setWaLink(`https://wa.me/${WHATSAPP}?text=${msg}`);

      dispatch({ type: "CLEAR" });
      setOrderId(res.id);
      // Auto-open WhatsApp immediately
      window.open(`https://wa.me/${WHATSAPP}?text=${msg}`, "_blank");
      setShowPopup(true);
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  // ── Thank you popup ──
  const ThankYouPopup = () => (
    <div className="popup-overlay">
      <div className="popup-card">
        <div className="popup-emoji">🎉</div>
        <h2>Thank You!</h2>
        <p>Your order <strong>#{orderId}</strong> has been placed successfully.</p>
        <p className="popup-sub">Your payment request will be approved within <strong>24 hours</strong>. You'll be able to track your order in your profile.</p>
        {waLink && (
          <a href={waLink} target="_blank" rel="noreferrer" className="btn-whatsapp">
            <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="wa" width={20} />
            Send Details on WhatsApp
          </a>
        )}
        <button className="popup-close-btn" onClick={() => { setShowPopup(false); setStep(2); }}>
          Track My Order
        </button>
      </div>
    </div>
  );

  // ── Awaiting approval screen ──
  if (step === 2) return (
    <div className="checkout-status-page">
      <div className="status-card awaiting">
        <FiClock className="status-icon spin" />
        <h2>Payment Under Review</h2>
        <p>Order <strong>#{orderId}</strong> has been placed.</p>
        <p>We've received your UTR <strong>{paymentRef}</strong> and are verifying your payment.</p>
        <div className="polling-bar"><div className="polling-fill" /></div>
        <p className="status-note">This page will update automatically once the admin approves or rejects your payment.</p>
        {waLink && (
          <a href={waLink} target="_blank" rel="noreferrer" className="btn-whatsapp" style={{ marginTop: "1.2rem" }}>
            <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="wa" width={20} />
            Send Details on WhatsApp
          </a>
        )}
      </div>
    </div>
  );

  // ── Approved screen ──
  if (step === 3) return (
    <div className="checkout-status-page">
      <div className="status-card approved">
        <FiCheckCircle className="status-icon" />
        <h2>Payment Approved! 🎉</h2>
        <p>Order <strong>#{orderId}</strong> is confirmed.</p>
        <p>Your order is now being processed and will be shipped soon.</p>
        <Link to="/account" className="btn-continue">View My Orders</Link>
        <Link to="/shop" className="btn-continue outline">Continue Shopping</Link>
      </div>
    </div>
  );

  // ── Rejected screen ──
  if (step === 4) return (
    <div className="checkout-status-page">
      <div className="status-card rejected">
        <FiXCircle className="status-icon" />
        <h2>Payment Not Verified</h2>
        <p>Order <strong>#{orderId}</strong> could not be confirmed.</p>
        <p>The UTR ID you provided could not be verified. Please contact us on WhatsApp.</p>
        <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" className="btn-continue">Contact on WhatsApp</a>
      </div>
    </div>
  );

  // ── Empty cart ──
  if (!cart.length) return (
    <div className="checkout-empty">
      <p>Your cart is empty.</p>
      <Link to="/shop" className="btn-continue">Shop Now</Link>
    </div>
  );

  return (
    <div className="checkout-page">
      {showPopup && <ThankYouPopup />}
      <Link to="/cart" className="back-link"><FiArrowLeft /> Back to Cart</Link>
      <h1>Checkout</h1>

      {/* Step indicator */}
      <div className="step-bar">
        {STEPS.map((s, i) => (
          <div key={s} className={`step-item ${i <= step ? "active" : ""} ${i < step ? "done" : ""}`}>
            <div className="step-circle">{i < step ? "✓" : i + 1}</div>
            <span>{s}</span>
          </div>
        ))}
      </div>

      <div className="checkout-layout">

        {/* ── STEP 0: Shipping ── */}
        {step === 0 && (
          <>
            <div className="checkout-form">
              <h2><FiUser /> Contact Information</h2>
              <div className="form-row-2">
                <div className={`field ${errors.fullName ? "error" : ""}`}>
                  <label>Full Name *</label>
                  <input name="fullName" value={form.fullName} onChange={handleChange} placeholder="e.g. Ayesha Khan" />
                  {errors.fullName && <span className="err-msg">{errors.fullName}</span>}
                </div>
                <div className={`field ${errors.phone ? "error" : ""}`}>
                  <label><FiPhone /> Phone Number *</label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="e.g. 03001234567" />
                  {errors.phone && <span className="err-msg">{errors.phone}</span>}
                </div>
              </div>
              <div className={`field ${errors.email ? "error" : ""}`}>
                <label><FiMail /> Email (optional)</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="ayesha@example.com" />
                {errors.email && <span className="err-msg">{errors.email}</span>}
              </div>

              <h2><FiMapPin /> Shipping Address</h2>
              <div className={`field ${errors.addressLine ? "error" : ""}`}>
                <label>Street Address / House No. *</label>
                <input name="addressLine" value={form.addressLine} onChange={handleChange} placeholder="House #12, Street 5, Block A" />
                {errors.addressLine && <span className="err-msg">{errors.addressLine}</span>}
              </div>
              <div className="form-row-2">
                <div className={`field ${errors.city ? "error" : ""}`}>
                  <label>City *</label>
                  <input name="city" value={form.city} onChange={handleChange} placeholder="Lahore" />
                  {errors.city && <span className="err-msg">{errors.city}</span>}
                </div>
                <div className="field">
                  <label>Postal Code</label>
                  <input name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="54000" />
                </div>
              </div>
            </div>

            {/* Order summary sidebar */}
            <div className="checkout-summary">
              <h2><FiPackage /> Order Summary</h2>
              <div className="order-items-list">
                {cart.map(item => (
                  <div key={`${item.id}-${item.size}-${item.color}`} className="order-item-row">
                    <img src={item.image} alt={item.name} />
                    <div className="oir-info">
                      <p>{item.name}</p>
                      <span>{item.size && `Size: ${item.size}`}{item.color && ` · ${item.color}`}</span>
                      <span>Qty: {item.qty}</span>
                    </div>
                    <strong>Rs. {(item.price * item.qty).toLocaleString()}</strong>
                  </div>
                ))}
              </div>
              <div className="summary-totals">
                <div className="sum-row"><span>Subtotal</span><span>Rs. {total.toLocaleString()}</span></div>
                <div className="sum-row"><span>Shipping</span><span className="free-tag">FREE</span></div>
                <div className="sum-row total-row"><span>Total</span><strong>Rs. {total.toLocaleString()}</strong></div>
              </div>
              <button className="btn-next" onClick={goToPayment}>Continue to Payment →</button>
            </div>
          </>
        )}

        {/* ── STEP 1: Payment ── */}
        {step === 1 && (
          <>
            <div className="checkout-form">
              <h2>Shipping To</h2>
              <div className="shipping-review">
                <p><strong>{form.fullName}</strong> · {form.phone}</p>
                <p>{form.addressLine}, {form.city}{form.postalCode ? " - " + form.postalCode : ""}</p>
                <button className="btn-edit-addr" onClick={() => setStep(0)}>Edit</button>
              </div>

              <h2>Pay via UPI</h2>
              <div className="upi-info-box">
                <div className="upi-detail"><span>UPI ID</span><strong>{UPI_ID}</strong></div>
                <div className="upi-detail"><span>Amount</span><strong className="amount-big">Rs. {total.toLocaleString()}</strong></div>
              </div>

              <div className="qr-section">
                <QRCodeSVG value={upiLink} size={180} level="M" />
                <p>Scan with any UPI app to pay</p>
              </div>

              <h3>Or open directly in your UPI app:</h3>
              <div className="upi-apps-grid">
                {UPI_APPS.map(app => (
                  <a
                    key={app.name}
                    href={buildUPILink(app.scheme, total, note)}
                    className={`upi-app-tile ${selectedApp === app.name ? "selected" : ""}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setSelectedApp(app.name)}
                  >
                    <img src={app.icon} alt={app.name} />
                    <span>{app.name}</span>
                  </a>
                ))}
              </div>

              <div className="field" style={{ marginTop: "1.2rem" }}>
                <label>UPI Transaction / UTR ID *</label>
                <input
                  value={paymentRef}
                  onChange={e => setPaymentRef(e.target.value.replace(/\D/g, "").slice(0, 12))}
                  placeholder="e.g. 123456789012"
                  maxLength={12}
                />
                <small>📌 Open your UPI app → check payment history → copy the 12-digit UTR/Transaction ID</small>
                {paymentRef.length > 0 && paymentRef.length < 12 && (
                  <span className="err-msg">UTR must be exactly 12 digits ({12 - paymentRef.length} remaining)</span>
                )}
              </div>

              {submitError && <p className="submit-error">{submitError}</p>}

              <button
                className="btn-confirm-pay"
                onClick={confirmPayment}
                disabled={submitting || paymentRef.length !== 12}
                style={{ opacity: paymentRef.length !== 12 ? 0.5 : 1, cursor: paymentRef.length !== 12 ? "not-allowed" : "pointer" }}
              >
                {submitting ? "Placing Order..." : "✅ Confirm & Place Order"}
              </button>
              <p className="pay-note">⚠️ Make sure you have completed the UPI payment before clicking above.</p>
            </div>

            <div className="checkout-summary">
              <h2><FiPackage /> Order Summary</h2>
              <div className="order-items-list">
                {cart.map(item => (
                  <div key={`${item.id}-${item.size}-${item.color}`} className="order-item-row">
                    <img src={item.image} alt={item.name} />
                    <div className="oir-info">
                      <p>{item.name}</p>
                      <span>{item.size && `Size: ${item.size}`}{item.color && ` · ${item.color}`}</span>
                    </div>
                    <strong>Rs. {(item.price * item.qty).toLocaleString()}</strong>
                  </div>
                ))}
              </div>
              <div className="summary-totals">
                <div className="sum-row"><span>Shipping</span><span className="free-tag">FREE</span></div>
                <div className="sum-row total-row"><span>Total</span><strong>Rs. {total.toLocaleString()}</strong></div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
